import EmptyState from "../../../../../shared/components/EmptyState";
import Spinner from "../../../../../shared/components/Spinner";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import { formatEventDateRangeLabel } from "../../../../../shared/utils/dateTime";
import type { MyEventRegistrationItem } from "../../../shared/services/registrationPaymentsService";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

export type RegistrationVisualStatus =
  | "all"
  | "confirmed"
  | "pending"
  | "waitlist"
  | "canceled"
  | "overdue";

export type RegistrationViewItem = MyEventRegistrationItem & {
  visualStatus: Exclude<RegistrationVisualStatus, "all">;
  visualStatusLabel: string;
  visualStatusHint: string;
  playerModeLabel: string;
};

type RegistrationStatusSummary = Record<RegistrationVisualStatus, number>;

type ProfileRegistrationsTabProps = {
  open: boolean;
  onToggle: () => void;
  loading: boolean;
  items: RegistrationViewItem[];
  filteredItems: RegistrationViewItem[];
  statusFilter: RegistrationVisualStatus;
  onStatusFilterChange: (value: RegistrationVisualStatus) => void;
  statusSummary: RegistrationStatusSummary;
  runningActionId: number | null;
  onCancelRegistration: (id: number) => void;
  onDeleteRegistration: (id: number) => void;
  gameTournamentIds?: Set<number>;
  onViewGames?: (tournamentId: number) => void;
};

function formatMoneyCents(amountCents: number, currency = "BRL") {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
  }).format(Number(amountCents || 0) / 100);
}

const REGISTRATION_FILTERS: RegistrationVisualStatus[] = [
  "all",
  "confirmed",
  "pending",
  "waitlist",
  "canceled",
  "overdue",
];

export default function ProfileRegistrationsTab({
  open,
  onToggle,
  loading,
  items,
  filteredItems,
  statusFilter,
  onStatusFilterChange,
  statusSummary,
  runningActionId,
  onCancelRegistration,
  onDeleteRegistration,
  gameTournamentIds,
  onViewGames,
}: ProfileRegistrationsTabProps) {
  return (
    <ProfileAccordionSection
      id="profile-registrations"
      icon="fact_check"
      title={siteTexts.profile.registrations.title}
      subtitle={siteTexts.profile.registrations.subtitle}
      open={open}
      onToggle={onToggle}
    >
      <div className="user-profile-content-section profile-registrations-content">
        <UserProfileSectionHeader
          className="profile-registrations-header"
          icon="receipt_long"
          title={siteTexts.profile.registrations.title}
          description={siteTexts.profile.registrations.subtitle}
        />
        {loading && (
          <p className="status-message">
            <Spinner label={siteTexts.common.loading} />
          </p>
        )}
        {!loading && items.length === 0 && (
          <EmptyState
            className="profile-empty-state"
            icon={
              <span className="material-symbols-outlined">receipt_long</span>
            }
            title={siteTexts.profile.registrations.empty}
          />
        )}
        {!loading && items.length > 0 && (
          <div className="profile-registrations-shell">
            <div
              className="profile-registrations-filters"
              role="tablist"
              aria-label={siteTexts.profile.registrations.filtersAriaLabel}
            >
              {REGISTRATION_FILTERS.map((filter) => (
                <button
                  key={`profile-registration-filter-${filter}`}
                  type="button"
                  className={`profile-registrations-filter${
                    filter === "confirmed"
                      ? " profile-registrations-filter--confirmed"
                      : filter === "pending"
                        ? " profile-registrations-filter--pending"
                        : filter === "waitlist"
                          ? " profile-registrations-filter--waitlist"
                          : filter === "canceled"
                            ? " profile-registrations-filter--canceled"
                            : filter === "overdue"
                              ? " profile-registrations-filter--overdue"
                              : ""
                  }${statusFilter === filter ? " is-active" : ""}`}
                  onClick={() => onStatusFilterChange(filter)}
                >
                  <span>{siteTexts.profile.registrations.filters[filter]}</span>
                  <span className="profile-registrations-filter-count">
                    {statusSummary[filter]}
                  </span>
                </button>
              ))}
            </div>

            <div className="profile-registrations-list">
              {filteredItems.map((item) => {
                const scheduleLabel = formatEventDateRangeLabel(
                  item.scheduledAt || item.createdAt,
                  item.scheduledEndAt,
                );
                const challengeTitle =
                  item.challengeName || item.challengeType || item.eventId;
                const canPayAgain =
                  (item.visualStatus === "pending" ||
                    item.visualStatus === "waitlist") &&
                  Boolean(String(item.paymentLink || "").trim());
                const canCancel =
                  (item.visualStatus === "pending" ||
                    item.visualStatus === "waitlist") &&
                  String(item.paymentStatus || "")
                    .trim()
                    .toLowerCase() !== "confirmed" &&
                  String(item.status || "")
                    .trim()
                    .toLowerCase() !== "confirmed";
                const canDelete =
                  item.visualStatus === "canceled" ||
                  item.visualStatus === "overdue";
                const tournamentId = Number(item.tournamentId || 0);
                const canViewGames =
                  Number.isFinite(tournamentId) &&
                  tournamentId > 0 &&
                  Boolean(gameTournamentIds?.has(tournamentId)) &&
                  Boolean(onViewGames);
                const isRunningAction =
                  Number(runningActionId) === Number(item.id);
                const teaser = String(
                  challengeTitle ||
                    siteTexts.challenges.publicDetail.labels.challengeFallback,
                )
                  .trim()
                  .slice(0, 2)
                  .toUpperCase();
                return (
                  <article
                    key={`my-registration-${item.id}`}
                    className="profile-registration-card"
                  >
                    <div className="profile-registration-thumb" aria-hidden="true">
                      <span>{teaser || "TN"}</span>
                    </div>

                    <div className="profile-registration-main">
                      <h3>{challengeTitle}</h3>
                      <div className="profile-registration-meta">
                        <span>{scheduleLabel}</span>
                        <span>{item.playerModeLabel}</span>
                      </div>
                      {String(item.teammateName || "").trim() && (
                        <p className="profile-registration-teammate">
                          {siteTexts.profile.registrations.teammatePrefix}{" "}
                          {item.teammateName}
                        </p>
                      )}
                    </div>

                    <div
                      className={`profile-registration-status profile-registration-status--${item.visualStatus}`}
                    >
                      <strong>{item.visualStatusLabel}</strong>
                      <span>{item.visualStatusHint}</span>
                    </div>

                    <div className="profile-registration-price">
                      {formatMoneyCents(Number(item.paymentAmountCents || 0))}
                    </div>

                    <div className="profile-registration-actions">
                      {canPayAgain && (
                        <a
                          className="profile-registration-pay-link"
                          href={item.paymentLink || "#"}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {siteTexts.profile.registrations.actions.payNow}
                        </a>
                      )}
                      {canCancel && (
                        <button
                          type="button"
                          className="profile-registration-cancel-button"
                          onClick={() => onCancelRegistration(Number(item.id))}
                          disabled={isRunningAction}
                        >
                          {isRunningAction
                            ? siteTexts.profile.registrations.actions.canceling
                            : siteTexts.profile.registrations.actions.cancel}
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="profile-registration-delete-button"
                          onClick={() => onDeleteRegistration(Number(item.id))}
                          disabled={isRunningAction}
                        >
                          {isRunningAction
                            ? siteTexts.profile.registrations.actions.removing
                            : siteTexts.profile.registrations.actions.remove}
                        </button>
                      )}
                      {canViewGames && (
                        <button
                          type="button"
                          className="profile-registration-games-button"
                          onClick={() => onViewGames?.(tournamentId)}
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            sports_tennis
                          </span>
                          {siteTexts.profile.registrations.actions.viewGames}
                        </button>
                      )}
                      <a
                        className="profile-registration-detail-link"
                        href={`#/desafios/detalhe/${item.tournamentId}`}
                      >
                        {siteTexts.profile.registrations.actions.viewDetails}
                      </a>
                    </div>
                  </article>
                );
              })}
              {filteredItems.length === 0 && (
                <EmptyState
                  className="profile-empty-state"
                  icon={
                    <span className="material-symbols-outlined">
                      filter_alt_off
                    </span>
                  }
                  title={siteTexts.profile.registrations.emptyByFilter}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </ProfileAccordionSection>
  );
}
